/**
 * Nutrition for the planner (docs/NUTRITION.md).
 *
 * Model: the recipe stores PER-SERVING values (`calories`, `protein_g`,
 * `carbs_g`, `fat_g`, all optional). A planned dish contributes
 * `porPorción × servings`, where the dish servings are
 * `entry.servings ?? recipe.servings ?? 1` — the SAME scaling semantics
 * as the shopping list (a dish without explicit servings = the entire
 * recipe at its default servings).
 *
 * Daily totals add only dishes WITH data; `missingCount` counts
 * dishes without nutrition so the UI can mark the total as partial
 * ("≈") instead of lying with a 0.
 */

export interface NutritionPerServing {
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
}

export interface NutritionTotals {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

export interface PlannedDishNutrition extends NutritionPerServing {
  /** Planned servings of the dish (entry.servings). */
  plannedServings: number | null;
  /** Default servings of the recipe (recipe.servings). */
  recipeServings: number | null;
}

export interface DayNutritionSummary {
  totals: NutritionTotals;
  /** Dishes that contributed data to the total. */
  countedDishes: number;
  /** Dishes with NO nutrition data at all (partial total if > 0). */
  missingCount: number;
}

/** Does the recipe have ANY nutrition data? */
export function hasNutritionData(n: NutritionPerServing): boolean {
  return (
    typeof n.calories === "number" ||
    typeof n.protein_g === "number" ||
    typeof n.carbs_g === "number" ||
    typeof n.fat_g === "number"
  );
}

/** Effective servings of a planned dish (shopping-list semantics). */
export function effectiveServings(
  plannedServings: number | null,
  recipeServings: number | null,
): number {
  if (
    typeof plannedServings === "number" &&
    Number.isFinite(plannedServings) &&
    plannedServings > 0
  ) {
    return plannedServings;
  }
  if (typeof recipeServings === "number" && Number.isFinite(recipeServings) && recipeServings > 0) {
    return recipeServings;
  }
  return 1;
}

/** Sums the nutrition of a day's dishes (scaled by servings). */
export function summarizeDayNutrition(dishes: PlannedDishNutrition[]): DayNutritionSummary {
  const totals: NutritionTotals = { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };
  let countedDishes = 0;
  let missingCount = 0;
  for (const dish of dishes) {
    if (!hasNutritionData(dish)) {
      missingCount += 1;
      continue;
    }
    const servings = effectiveServings(dish.plannedServings, dish.recipeServings);
    totals.calories += (dish.calories ?? 0) * servings;
    totals.protein_g += (dish.protein_g ?? 0) * servings;
    totals.carbs_g += (dish.carbs_g ?? 0) * servings;
    totals.fat_g += (dish.fat_g ?? 0) * servings;
    countedDishes += 1;
  }
  return { totals, countedDishes, missingCount };
}

/** Stable rounding for display (1 decimal, no -0 or floating-point noise). */
export function roundNutrition(value: number): number {
  const rounded = Math.round(value * 10) / 10;
  return Object.is(rounded, -0) ? 0 : rounded;
}

/** Sanitized numeric value from nutrition input ("" → null, decimal comma). */
export function parseNutritionInput(value: string): number | null {
  const trimmed = value.trim().replace(",", ".");
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}
